import { useEffect } from 'react';

export const SITE_NAME = 'SnapCamp';
export const SITE_URL = 'https://www.snapcamp.ai';
export const DEFAULT_TITLE = 'SnapCamp | Kids Summer Camp Marketplace';
export const DEFAULT_DESCRIPTION =
    'SnapCamp is the kids summer camp marketplace where parents find and book trusted camps, and camp owners manage registrations, families, staff, and growth in one place.';
export const DEFAULT_OG_IMAGE = 'https://www.snapcamp.ai/og-image.jpg';
export const DEFAULT_KEYWORDS =
    'kids summer camps, summer camp marketplace, trusted summer camps, parents booking camps, camp owner software, camp registrations, family camp management, youth camps';

export interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    canonical?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    ogType?: string;
    ogUrl?: string;
    jsonLd?: object | object[];
    noIndex?: boolean;
}

function upsertMeta(
    selector: string,
    createAttrs: Record<string, string>,
    contentAttr: string,
    content: string
) {
    if (typeof document === 'undefined') return;
    let el = document.head.querySelector(selector) as HTMLElement | null;
    if (!el) {
        el = document.createElement('meta');
        Object.entries(createAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
    }
    el.setAttribute(contentAttr, content);
}

function upsertLink(rel: string, href: string) {
    if (typeof document === 'undefined') return;
    let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
    }
    el.href = href;
}

function removeMeta(selector: string) {
    if (typeof document === 'undefined') return;
    document.head.querySelector(selector)?.remove();
}

/**
 * Manages per-route SEO: document title, meta description, Open Graph,
 * Twitter Card, canonical URL, noindex, and JSON-LD structured data.
 *
 * Call this hook at the top of any screen component. Pass only the fields
 * that differ from the defaults — everything else falls back automatically.
 */
export default function useSEO({
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = 'website',
    ogUrl,
    jsonLd,
    noIndex = false,
}: SEOProps = {}) {
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const resolvedTitle = title ?? DEFAULT_TITLE;
        const resolvedDesc = description ?? DEFAULT_DESCRIPTION;
        const resolvedOgTitle = ogTitle ?? resolvedTitle;
        const resolvedOgDesc = ogDescription ?? resolvedDesc;
        const resolvedOgImage = ogImage ?? DEFAULT_OG_IMAGE;
        const resolvedOgUrl = ogUrl ?? canonical ?? SITE_URL;

        document.title = resolvedTitle;

        upsertMeta('meta[name="description"]', { name: 'description' }, 'content', resolvedDesc);

        if (keywords) {
            upsertMeta('meta[name="keywords"]', { name: 'keywords' }, 'content', keywords);
        }

        // Open Graph
        upsertMeta('meta[property="og:title"]', { property: 'og:title' }, 'content', resolvedOgTitle);
        upsertMeta('meta[property="og:description"]', { property: 'og:description' }, 'content', resolvedOgDesc);
        upsertMeta('meta[property="og:image"]', { property: 'og:image' }, 'content', resolvedOgImage);
        upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'content', ogType);
        upsertMeta('meta[property="og:url"]', { property: 'og:url' }, 'content', resolvedOgUrl);
        upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'content', SITE_NAME);

        // Twitter Card
        upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'content', 'summary_large_image');
        upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', resolvedOgTitle);
        upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', resolvedOgDesc);
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', resolvedOgImage);
        upsertMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, 'content', '@SnapCampApp');

        // Canonical URL
        if (canonical) {
            upsertLink('canonical', canonical);
        }

        // noindex / robots
        if (noIndex) {
            upsertMeta('meta[name="robots"]', { name: 'robots' }, 'content', 'noindex, nofollow');
        } else {
            removeMeta('meta[name="robots"][content="noindex, nofollow"]');
        }

        // JSON-LD structured data — remove previously injected schemas and replace
        document.head
            .querySelectorAll('script[type="application/ld+json"][data-seo]')
            .forEach(s => s.remove());

        if (jsonLd) {
            const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            schemas.forEach(schema => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-seo', 'route');
                script.textContent = JSON.stringify(schema);
                document.head.appendChild(script);
            });
        }
    }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, ogUrl, noIndex]);
}
